import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { config } from '../config';
import { Router } from '@angular/router';
import { StoreService } from './store.service';
import { jwtDecode } from 'jwt-decode';

// Login response interfaces
interface LoginResponse {
  status: 'success' | 'error';
  msg?: string;
  token?: string;
  data?: SystemUser;
}

interface SystemUser {
  _id?: string;
  email: string;
  user_role: number; // 0 = admin, 1 = restaurant owner
  restaurant_id?: string;
  name?: string;
  mobile?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<SystemUser | null>;
  public currentUser: Observable<SystemUser | null>;
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';

  constructor(
    private http: HttpClient,
    private router: Router,
    private store: StoreService
  ) {
    const savedUser = this.getStoredUser();
    this.currentUserSubject = new BehaviorSubject<SystemUser | null>(savedUser);
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): SystemUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Authenticate system user with email and password
   */
  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${config.API_URL}v1/systemUserLogin`, { email, password })
      .pipe(
        map(response => {
          if (response.status === 'success' && response.token && response.data) {
            this.storeAuthData(response.data, response.token);
            this.currentUserSubject.next(response.data);
          }
          return response;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Store authentication token and user data in session storage
   */
  private storeAuthData(user: SystemUser, token: string): void {
    sessionStorage.setItem(this.TOKEN_KEY, token);
    sessionStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Retrieve stored user from session storage
   */
  private getStoredUser(): SystemUser | null {
    try {
      const userJson = sessionStorage.getItem(this.USER_KEY);
      return userJson ? JSON.parse(userJson) : null;
    } catch (e) {
      console.warn('Failed to parse stored user:', e);
      return null;
    }
  }

  /**
   * Get authentication token
   */
  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!(token && this.isTokenValid(token));
  }

  /**
   * Validate JWT token expiration
   */
  isTokenValid(token: string): boolean {
    try {
      const decoded = jwtDecode<any>(token);
      return decoded.exp ? decoded.exp * 1000 > Date.now() : true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get decoded user from token
   */
  getDecodedUser(): any | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      return jwtDecode(token);
    } catch (e) {
      console.error('Invalid token:', e);
      return null;
    }
  }

  /**
   * Get user role (0=admin, 1=restaurant owner)
   */
  getUserRole(): number | null {
    return (this.currentUserValue && this.currentUserValue.user_role) || null;
  }

  /**
   * Determine dashboard route based on user role
   */
  getDefaultDashboard(): string {
    const role = this.getUserRole();
    return role === 0 ? '/admin/dashboard' : '/restaurant/dashboard';
  }

  /**
   * Logout user and clear stored data
   */
  logout(): void {
    const currentUser = this.currentUserValue;
    const token = this.getToken();

    if (currentUser && currentUser._id && token) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });

      this.http.post<any>(`${config.API_URL}v1/auth/systemUserLogout`, { _id: currentUser._id }, { headers })
        .subscribe(
          () => {},
          () => {}
        );
    }

    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    this.store.setLoginData({ data: null, token: null, is_logged: false });
    this.currentUserSubject.next(null);
    this.router.navigate(['/']);
  }

  /**
   * Handle HTTP errors with standard format
   */
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An error occurred during authentication';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else if (error.status === 401) {
      errorMessage = 'Invalid email or password';
    } else if (error.status === 403) {
      errorMessage = 'Access denied';
    } else if (error.status === 500) {
      errorMessage = 'Server error. Please try again later.';
    } else if (error.error && error.error.msg) {
      errorMessage = error.error.msg;
    }

    return throwError({ status: 'error', msg: errorMessage });
  }
}
